import type { DeliveryMethod, DeliveryMethodType, VariantDeliveryMethods } from '@libs/common-types';

import type { YesNo } from '../types';
import { getDeliveryMethodAvailability } from './get-delivery-method-availability';
import { getDeliveryMethodStockAvailability } from './get-delivery-method-stock-availability';
import type { StockAvailability } from './validate-stock-status';

const DELIVERY_METHODS: DeliveryMethodType[] = ['STH', 'BOPIS', 'SDD'];

type BuildDeliveryMethodDataParams = {
    deliveryMethods: { [K in DeliveryMethodType]?: DeliveryMethod };
    tailoredVariant: Maybe<VariantDeliveryMethods>;
    purchaseOption: 'one-time' | 'autoship';
    isCustomPharmacyOOS: boolean;
};

type DeliveryMethodResult = {
    sth_availability: YesNo;
    sth_stock_availability: Nullable<StockAvailability>;
    bopis_availability: YesNo;
    bopis_stock_availability: Nullable<StockAvailability>;
    sdd_availability: YesNo;
    sdd_stock_availability: Nullable<StockAvailability>;
};

export function buildDeliveryAvailability({
    deliveryMethods,
    tailoredVariant,
    purchaseOption,
    isCustomPharmacyOOS,
}: BuildDeliveryMethodDataParams): DeliveryMethodResult {
    return DELIVERY_METHODS.reduce(
        (acc, method) => {
            const methodData = deliveryMethods[method];
            const methodPrefix = method.toLowerCase() as Lowercase<DeliveryMethodType>;

            acc[`${methodPrefix}_availability`] = getDeliveryMethodAvailability({
                availablePurchaseOptions: methodData?.availablePurchaseOptions,
                purchaseOption,
            });
            acc[`${methodPrefix}_stock_availability`] = getDeliveryMethodStockAvailability({
                purchaseOption,
                isCustomPharmacyOOS,
                minimumCaseQty: methodData?.purchaseQuantities?.min ?? 1,
                availableStock: tailoredVariant?.[method]?.availableStock,
                availablePurchaseOptions: methodData?.availablePurchaseOptions,
                ...(method === 'SDD' && {
                    isDoordashAvailable: tailoredVariant?.SDD?.doorDashValidationSuccess ?? false,
                }),
            });

            return acc;
        },
        {} as Record<string, unknown>
    ) as DeliveryMethodResult;
}
